import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, fmtMoney, type JarayondaItem, type JarayondaSale } from "../api";
import { useBranch } from "../lib/use-branch";
import { Button } from "./ui/button";
import { Dialog } from "./ui/dialog";
import { QtyStepper } from "./qty-stepper";

/** Miqdorlar 3 kasrgacha — backenddagi `qty3` bilan bir xil aniqlik. */
function qty3(x: number): number {
  return Math.round(x * 1000) / 1000;
}

type ItemLine = { product_id: number; quantity: string };

export type PartialPayload =
  | { kind: "cancel" }
  | { kind: "confirm"; sold_items: ItemLine[]; returned_items: ItemLine[] };

/**
 * Band qilingan qatorlardan `tasdiqlash` so'rovini yig'adi.
 *
 * Backend har tovar uchun `sotilgan + qaytgan = band qilingan` shartini talab
 * qiladi (`jarayonda_qty_mismatch`), shuning uchun ikkala ro'yxat ham TEGILGAN
 * emas, BARCHA band qilingan tovarlardan quriladi. Nol miqdorli qator
 * yuborilmaydi — `MobileItemIn` `quantity > 0` ni talab qiladi.
 *
 * Hammasi qaytarilsa nol summali sotuv yozilmaydi: bandlik butunlay bekor
 * qilinadi (`kind: "cancel"` → `PATCH /mobile/sales/{id}/bekor`).
 *
 * Sof funksiya — bu paketda JS test runner yo'q, shuning uchun qaror mantiqi
 * UI'dan ajratilgan holda ko'rib chiqiladi.
 */
export function buildPartialPayload(
  items: JarayondaItem[],
  confirmed: Record<number, string>,
): PartialPayload {
  const sold_items: ItemLine[] = [];
  const returned_items: ItemLine[] = [];

  for (const it of items) {
    const reserved = qty3(Number(it.quantity) || 0);
    const raw = Number(confirmed[it.product_id] ?? 0);
    // Chegaradan chiqqan/noto'g'ri kiritma bu yerda ham qisiladi (QtyStepper
    // allaqachon qisadi — bu ikkinchi himoya).
    const sold = qty3(Math.min(Math.max(Number.isFinite(raw) ? raw : 0, 0), reserved));
    const returned = qty3(reserved - sold);
    if (sold > 0) {
      sold_items.push({ product_id: it.product_id, quantity: String(sold) });
    }
    if (returned > 0) {
      returned_items.push({ product_id: it.product_id, quantity: String(returned) });
    }
  }

  if (sold_items.length === 0) return { kind: "cancel" };
  return { kind: "confirm", sold_items, returned_items };
}

/**
 * Qisman qaytarish dialogi — jarayondagi sotuvning bir qismini tasdiqlab,
 * qolganini bandlikdan bo'shatadi.
 *
 * Muhim: band qilishda zaxira KAMAYMAGAN edi (`stock_movement` yozilmaydi,
 * faqat `sales.jarayondagi_miqdor` xaritasi tovarni ushlab turadi), shuning
 * uchun qaytgan qism uchun kompensatsion KIRIM yozilmaydi — u bo'lsa zaxira
 * ikki marta hisoblanib ketardi. Bandlik `jarayondagi_miqdor` tozalanishi
 * bilan bo'shaydi; zaxiradan faqat tasdiqlangan qism chiqadi.
 *
 * Narx tovar kartochkasidan (`sotuv_narx`) — bandlik qatorida narx saqlanmaydi,
 * shuning uchun summa taxminiy: aniq summani server tasdiqlashda hisoblaydi.
 */
export function JarayondaPartialDialog({
  open,
  onOpenChange,
  sale,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sale: JarayondaSale;
  onDone: () => void;
}) {
  const { t } = useTranslation(["ombor", "common"]);
  const { selectedBranchId } = useBranch();
  // Sukut bo'yicha hammasi tasdiqlanadi — eng ko'p uchraydigan holat.
  const [confirmed, setConfirmed] = useState<Record<number, string>>(() =>
    Object.fromEntries(sale.items.map((i) => [i.product_id, String(Number(i.quantity))])),
  );
  const [prices, setPrices] = useState<Record<number, number>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Bandlik qatorida narx yo'q — tovar kartochkasidan olamiz (JarayondaForm
  // ham shunday qiladi). Narx kelmasa dialog baribir ishlaydi, summa 0 ko'rinadi.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const products = await api.products({
          faol: true,
          branch_id: selectedBranchId ?? undefined,
        });
        if (!alive) return;
        setPrices(
          Object.fromEntries(products.map((p) => [p.id, Number(p.sotuv_narx) || 0])),
        );
      } catch {
        /* narx — ixtiyoriy ko'rgazma, xato dialogni buzmaydi */
      }
    })();
    return () => {
      alive = false;
    };
  }, [selectedBranchId]);

  const payload = useMemo(
    () => buildPartialPayload(sale.items, confirmed),
    [sale.items, confirmed],
  );
  // Bo'sh maydon = 0 (hammasi qaytadi) — bu haqiqiy tanlov. Lekin son bo'lmagan
  // kiritma 0 deb o'qilib, jimgina bekor qilishga olib kelmasligi kerak.
  const invalid = useMemo(
    () =>
      sale.items.some((i) => {
        const raw = (confirmed[i.product_id] ?? "").trim();
        return raw !== "" && !Number.isFinite(Number(raw));
      }),
    [sale.items, confirmed],
  );
  const allReturned = !invalid && payload.kind === "cancel";

  const estTotal = useMemo(
    () =>
      sale.items.reduce((s, i) => {
        const reserved = qty3(Number(i.quantity) || 0);
        const sold = Math.min(Math.max(Number(confirmed[i.product_id]) || 0, 0), reserved);
        return s + sold * (prices[i.product_id] ?? 0);
      }, 0),
    [sale.items, confirmed, prices],
  );

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      if (payload.kind === "cancel") {
        // Nol summali sotuv yaratmaymiz — bandlikni butunlay bekor qilamiz.
        await api.cancelJarayonda(sale.id);
        toast.success(t("jarayonda.cancelled"));
      } else {
        await api.confirmJarayonda(sale.id, {
          sold_items: payload.sold_items,
          returned_items: payload.returned_items,
        });
        toast.success(t("jarayonda.partial.saved"));
      }
      onDone();
      onOpenChange(false);
    } catch (e) {
      setErr((e as { message?: string }).message || t("common:common.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !busy && onOpenChange(o)}
      contentClassName="max-w-xl"
    >
      <div className="space-y-4 p-5">
        <div>
          <h2 className="pr-8 text-lg font-semibold leading-none tracking-tight">
            {t("jarayonda.partial.title")}
          </h2>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {sale.created_by_name || "—"}
            {sale.izoh ? ` · ${sale.izoh}` : ""}
          </p>
        </div>

        <div className="max-h-[45vh] overflow-y-auto pr-1">
          <table className="w-full table-fixed text-sm">
            <colgroup>
              <col />
              <col className="w-[70px]" />
              <col className="w-[110px]" />
              <col className="w-[90px]" />
            </colgroup>
            <thead className="sticky top-0 z-10 bg-muted/95 backdrop-blur">
              <tr className="border-b text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-2 text-left">
                  {t("jarayonda.partial.nomi")}
                </th>
                <th className="px-2 py-2 text-center">
                  {t("jarayonda.partial.band")}
                </th>
                <th className="px-2 py-2 text-center">
                  {t("jarayonda.partial.tasdiqlanadi")}
                </th>
                <th className="px-3 py-2 text-right">
                  {t("jarayonda.partial.qaytariladi")}
                </th>
              </tr>
            </thead>
            <tbody>
              {sale.items.map((i) => {
                const reserved = qty3(Number(i.quantity) || 0);
                const sold = Math.min(
                  Math.max(Number(confirmed[i.product_id]) || 0, 0),
                  reserved,
                );
                const back = qty3(reserved - sold);
                return (
                  <tr key={i.product_id} className="border-b last:border-0">
                    <td className="px-3 py-2">
                      <div className="truncate font-medium" title={i.product_nomi}>
                        {i.product_nomi}
                      </div>
                    </td>
                    <td className="px-2 py-2 text-center tabular-nums text-muted-foreground">
                      {reserved}
                    </td>
                    <td className="px-2 py-2 text-center">
                      <QtyStepper
                        value={confirmed[i.product_id] ?? "0"}
                        onChange={(v) =>
                          setConfirmed((prev) => ({ ...prev, [i.product_id]: v }))
                        }
                        max={reserved}
                        disabled={busy}
                      />
                    </td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">
                      {back > 0 ? (
                        back
                      ) : (
                        <span className="font-normal text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="space-y-1 rounded-md border bg-muted/30 px-3 py-2.5">
          <div className="flex items-baseline justify-between">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">
              {t("jarayonda.partial.estTotal")}
            </span>
            <span className="font-medium tabular-nums">{fmtMoney(estTotal)}</span>
          </div>
          <p className="text-xs text-muted-foreground">
            {allReturned
              ? t("jarayonda.partial.allReturnedNote")
              : t("jarayonda.partial.estNote")}
          </p>
        </div>

        {invalid && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {t("jarayonda.partial.invalidQty")}
          </div>
        )}

        {err && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {err}
          </div>
        )}

        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className="flex-none"
          >
            {t("jarayonda.partial.cancel")}
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={busy || invalid}
            className="flex-1"
          >
            {busy
              ? t("jarayonda.form.submitting")
              : t("jarayonda.partial.submit")}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
